import { useState, useEffect, useRef } from 'react';
import { log } from '@/lib/logger';

interface ExtensionDetectionOptions {
  poll?: boolean;
  intervalMs?: number;
}

interface ExtensionDetectionResult {
  detected: boolean;
  checking: boolean;
}

/**
 * The extension content script injects a shadow DOM host with this ID into every
 * page (including the dashboard). Checking the DOM is reliable regardless of
 * extension ID (CWS vs unpacked dev builds have different IDs).
 *
 * It is NOT reliable across the content script's lifetime, which is why it is
 * no longer the only signal. The host is created by an async, kill-switch-braked
 * pre-warm at document_idle, and `teardownContentScript()` REMOVES it when the
 * extension is reloaded out from under a live tab — so a tab could read
 * "not installed" at a user whose extension is running (reported by Emily
 * 2026-08-24: install card + banner shown with the unpacked build loaded; same
 * class as the 2026-08-16 team-review incident).
 */
const OVERLAY_HOST_ID = 'overlayqa-overlay-host';

/**
 * Mirror of `CLICK_PROBE_FLAG_ATTR` in
 * `packages/extension/src/utils/clickProbeAttrs.ts`, the attribute
 * `clickProbeEntry.ts` stamps on `<html>` at document_start in the MAIN world
 * on every page (every build since v1.2.28). It exists before React can mount
 * and, being plain DOM state, survives extension reloads and content-script
 * teardown. Sanctioned mirror like `review-app/lib/extensionPresence.ts`;
 * pinned to the extension source by `__tests__/useExtensionDetection.test.tsx`.
 *
 * The two signals are OR'd — widened, never narrowed. A tab that never received
 * a content script at all (opened before install, or while the extension was
 * broken) has neither signal; no in-page probe can see past that.
 */
export const EXTENSION_PRESENCE_ATTR = 'data-overlayqa-click-probe';

export function useExtensionDetection(
  options: ExtensionDetectionOptions = {}
): ExtensionDetectionResult {
  const { poll = false, intervalMs = 3000 } = options;
  const [detected, setDetected] = useState(false);
  const [checking, setChecking] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    function probe() {
      const found =
        !!document.getElementById(OVERLAY_HOST_ID) ||
        document.documentElement.hasAttribute(EXTENSION_PRESENCE_ATTR);
      log.info('[useExtensionDetection] DOM probe', { detected: found });
      setDetected(found);
      setChecking(false);

      if (found && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    probe();

    if (poll) {
      intervalRef.current = setInterval(probe, intervalMs);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [poll, intervalMs]);

  return { detected, checking };
}
